// Cases split into "action required" (blocked on the user) vs the rest. Grouped
// off the per-event `actionRequired` signal, not unread: the actions list also
// includes merely-unread staff messages, which are not action-required.

export type CaseAction = { actionRequired: boolean };

export type CaseLike = { actions?: readonly CaseAction[] | null };

export function isActionRequired(caseItem: CaseLike): boolean {
  return caseItem.actions?.some(action => action.actionRequired) ?? false;
}

// Drives the attention alert.
export function countBlockedCases(cases: readonly CaseLike[]): number {
  return cases.reduce(
    (count, caseItem) => count + (isActionRequired(caseItem) ? 1 : 0),
    0,
  );
}

// CHAT events only enter `actions` when unseen-by-user or action-required (see
// Issue.actions in dbschema/issue.gel), so a non-action-required CHAT action is
// exactly an unread staff message.
type ChatAwareCase = {
  actions?: readonly { actionRequired: boolean; eventType: string }[] | null;
};

export function hasUnreadStaffChat(caseItem: ChatAwareCase): boolean {
  return (
    caseItem.actions?.some(
      action => action.eventType === "CHAT" && !action.actionRequired,
    ) ?? false
  );
}

// Unread-only (excludes action-required cases) so blocked + unread partition the
// attention-worthy set — analytics can sum them for the union.
export function countUnreadCases(cases: readonly ChatAwareCase[]): number {
  return cases.reduce(
    (count, caseItem) =>
      count +
      (!isActionRequired(caseItem) && hasUnreadStaffChat(caseItem) ? 1 : 0),
    0,
  );
}

// Input order is preserved within each band; sorting and capping are the list's job.
export function bandCases<T extends CaseLike>(
  cases: readonly T[],
): { actionRequired: T[]; inProgress: T[] } {
  const actionRequired: T[] = [];
  const inProgress: T[] = [];
  for (const caseItem of cases) {
    (isActionRequired(caseItem) ? actionRequired : inProgress).push(caseItem);
  }
  return { actionRequired, inProgress };
}
